import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FramedPane } from '@/components/FramedPane';
import { FramedSearchPane } from '@/components/FramedSearchPane';
import { usePageTitle } from '@/components/layout/page-title';
import { PageHeader } from '@/components/layout/PageHeader';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { formatDateTime } from '@/features/invoices/format';
import { cn } from '@/lib/utils';
import { CustomerFormDialog } from './CustomerFormDialog';
import { formatAddressLines } from './format';
import { useCustomers, useDeleteCustomer, type Customer } from './api';

export function CustomersPage() {
  const { t } = useTranslation();
  usePageTitle(t('customers.title'));

  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setQuery(input), 300);
    return () => clearTimeout(id);
  }, [input]);

  const customers = useCustomers(query);
  const del = useDeleteCustomer();

  const items = useMemo(() => {
    const seen = new Set<number>();
    const flat: Customer[] = [];
    for (const page of customers.data?.pages ?? []) {
      for (const customer of page.items) {
        if (!seen.has(customer.id)) {
          seen.add(customer.id);
          flat.push(customer);
        }
      }
    }
    return flat;
  }, [customers.data]);

  const selected = items.find((customer) => customer.id === selectedId) ?? null;

  const loadMoreRef = useInfiniteScroll<HTMLLIElement>(
    () => void customers.fetchNextPage(),
    customers.hasNextPage && !customers.isFetchingNextPage,
  );
  const triggerIndex = Math.max(0, items.length - 5);

  const onDelete = (customer: Customer) => {
    del.mutate(customer.id, {
      onSuccess: () => {
        toast.success(t('customers.toasts.deleted'));
        setSelectedId((current) => (current === customer.id ? null : current));
      },
      onError: () => toast.error(t('customers.toasts.delete_failed')),
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <PageHeader
        title={t('customers.title')}
        description={t('customers.description')}
        actions={
          <CustomerFormDialog
            mode={{ kind: 'create' }}
            onSaved={(customer) => setSelectedId(customer.id)}
          >
            <Button type="button">
              <Plus data-icon="inline-start" />
              {t('customers.add')}
            </Button>
          </CustomerFormDialog>
        }
      />

      <div className="grid min-h-0 flex-1 gap-6 md:grid-cols-[22rem_1fr]">
        <FramedSearchPane
          search={input}
          onSearchChange={setInput}
          searchPlaceholder={t('customers.search_placeholder')}
          listLabel={t('customers.title')}
          status={
            customers.isPending
              ? 'pending'
              : customers.isError
                ? 'error'
                : items.length === 0
                  ? 'empty'
                  : 'ready'
          }
          errorMessage={t('customers.load_error')}
          emptyMessage={t('customers.empty')}
        >
          {items.map((customer, i) => (
            <li key={customer.id} ref={i === triggerIndex ? loadMoreRef : undefined}>
              <CustomerButton
                customer={customer}
                selected={customer.id === selectedId}
                onSelect={() => setSelectedId(customer.id)}
              />
            </li>
          ))}
          {customers.isFetchingNextPage ? (
            <li className="py-2 text-center text-sm text-muted-foreground">
              {t('customers.loading_more')}
            </li>
          ) : null}
        </FramedSearchPane>

        {selected ? (
          <FramedPane scroll>
            <CustomerDetail customer={selected} onDelete={() => onDelete(selected)} />
          </FramedPane>
        ) : (
          <FramedPane>
            <p className="flex min-h-0 flex-1 items-center justify-center text-sm text-muted-foreground">
              {t('customers.no_selection')}
            </p>
          </FramedPane>
        )}
      </div>
    </div>
  );
}

function CustomerButton({
  customer,
  selected,
  onSelect,
}: {
  customer: Customer;
  selected: boolean;
  onSelect: () => void;
}) {
  const subtitle = [customer.nip, customer.address.postTown].filter(Boolean).join(' · ');
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'w-full rounded-md px-3 py-2 text-left transition-colors hover:bg-muted',
        selected && 'bg-muted',
      )}
    >
      <div className="truncate text-sm font-medium">{customer.name}</div>
      {subtitle !== '' ? (
        <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
      ) : null}
    </button>
  );
}

function CustomerDetail({ customer, onDelete }: { customer: Customer; onDelete: () => void }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const address = formatAddressLines(customer.address).join('\n');

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-lg font-semibold">{customer.name}</h2>
        <div className="flex shrink-0 gap-2">
          <CustomerFormDialog mode={{ kind: 'edit', customer }}>
            <Button type="button" variant="outline">
              <Pencil data-icon="inline-start" />
              {t('customers.edit')}
            </Button>
          </CustomerFormDialog>
          <ConfirmDialog
            title={t('customers.confirm.delete_title')}
            description={t('customers.confirm.delete_description')}
            confirmLabel={t('customers.delete')}
            destructive
            onConfirm={onDelete}
          >
            <Button type="button" variant="ghost" className="text-destructive">
              <Trash2 data-icon="inline-start" />
              {t('customers.delete')}
            </Button>
          </ConfirmDialog>
        </div>
      </div>

      <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[8rem_1fr]">
        {customer.nip ? <DetailRow label={t('customers.detail.nip')} value={customer.nip} /> : null}
        {customer.regon ? (
          <DetailRow label={t('customers.detail.regon')} value={customer.regon} />
        ) : null}
        <DetailRow label={t('customers.detail.address')} value={address} />
        {customer.email ? (
          <DetailRow label={t('customers.detail.email')} value={customer.email} />
        ) : null}
        {customer.phone ? (
          <DetailRow label={t('customers.detail.phone')} value={customer.phone} />
        ) : null}
        <DetailRow label={t('customers.detail.created')} value={formatDateTime(lng, customer.createdAt)} />
        <DetailRow label={t('customers.detail.updated')} value={formatDateTime(lng, customer.updatedAt)} />
      </dl>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium whitespace-pre-line">{value}</dd>
    </>
  );
}
